import {
  countRestartedRealtimeForSection,
  type DetailsRealtimeRow,
  responseSectionId,
} from "@/utils/testDetailsResponses";

export const ANALYSIS_TELEMETRY_QUESTIONS = new Set([
  "nextsection",
  "nextpage",
  "restarted",
  "longgap",
  "connectionlost",
  "connectionrestored",
  "timeout",
]);

export function isAnswerLikeRealtimeQuestion(question: string | null | undefined): boolean {
  const key = String(question || "").trim().toLowerCase();
  if (!key) return false;
  return !ANALYSIS_TELEMETRY_QUESTIONS.has(key);
}

export function parseApiTimeMs(value: string | Date | null | undefined): number {
  if (!value) return 0;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : 0;
  }

  const raw = String(value).trim();
  if (!raw) return 0;
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
  const ms = Date.parse(hasZone ? raw : `${raw}Z`);
  return Number.isFinite(ms) ? ms : 0;
}

export function realtimeEffectiveMs(row: {
  clientTime?: string | Date | null;
  time?: string | Date | null;
}): number {
  const raw = row?.clientTime || row?.time;
  return parseApiTimeMs(raw);
}

export function formatDurationMmSs(durationMs: number | null | undefined): string {
  if (durationMs == null || !Number.isFinite(durationMs) || durationMs < 0) return "";
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export type SectionAnalysisSection = {
  sectionId?: number | string | null;
  name?: string | null;
};

export type SectionAnalysisRow = {
  sectionId: number;
  sectionName: string;
  restarts: number;
  startIso: string | null;
  endIso: string | null;
  durationMmSs: string;
  initialPauseMmSs: string;
  longestPauseMmSs: string;
};

function toIsoOrNull(ms: number): string | null {
  if (!ms || !Number.isFinite(ms)) return null;
  return new Date(ms).toISOString();
}

function parseStartMs(startTime: string | Date | null | undefined): number {
  return parseApiTimeMs(startTime);
}

function sortRealtime(rows: DetailsRealtimeRow[]): DetailsRealtimeRow[] {
  return [...rows].sort((a, b) => {
    const delta = realtimeEffectiveMs(a) - realtimeEffectiveMs(b);
    if (delta !== 0) return delta;
    return Number(a?.id ?? 0) - Number(b?.id ?? 0);
  });
}

export function buildSectionAnalysisRows(args: {
  sections: SectionAnalysisSection[] | null | undefined;
  realtimeResponses: DetailsRealtimeRow[] | null | undefined;
  startTime?: string | Date | null;
}): SectionAnalysisRow[] {
  const sections = Array.isArray(args.sections) ? args.sections : [];
  const realtime = Array.isArray(args.realtimeResponses) ? args.realtimeResponses : [];
  const testStartMs = parseStartMs(args.startTime);

  const endBySectionId = new Map<number, { ms: number; id: number }>();
  for (const row of realtime) {
    const sid = responseSectionId(row);
    if (!sid) continue;
    const q = String(row?.question || "").trim().toLowerCase();
    if (q !== "nextsection") continue;
    const ms = realtimeEffectiveMs(row);
    if (!ms) continue;
    const id = Number(row?.id ?? 0);
    const prev = endBySectionId.get(sid);
    if (!prev || ms > prev.ms || (ms === prev.ms && id > prev.id)) {
      endBySectionId.set(sid, { ms, id });
    }
  }

  let previousEndMs = 0;
  return sections.map((section, index) => {
    const sectionId = Number(section?.sectionId ?? 0);
    const endMs = sectionId ? (endBySectionId.get(sectionId)?.ms ?? 0) : 0;
    const startMs = index === 0 ? testStartMs : previousEndMs;
    previousEndMs = endMs;

    const startIso = toIsoOrNull(startMs);
    const endIso = toIsoOrNull(endMs);
    const durationMmSs =
      startMs && endMs && endMs >= startMs ? formatDurationMmSs(endMs - startMs) : "";

    const answerLike = sortRealtime(
      realtime.filter(
        (row) =>
          responseSectionId(row) === sectionId &&
          isAnswerLikeRealtimeQuestion(row?.question)
      )
    );

    let initialPauseMmSs = "";
    if (startMs && answerLike.length > 0) {
      const firstMs = realtimeEffectiveMs(answerLike[0]);
      // Empty can indicate client/server clock skew or another negative interval.
      if (firstMs >= startMs) initialPauseMmSs = formatDurationMmSs(firstMs - startMs);
    }

    let longestPauseMmSs = "";
    if (answerLike.length >= 2) {
      let maxGap = 0;
      for (let i = 1; i < answerLike.length; i++) {
        const gap = realtimeEffectiveMs(answerLike[i]) - realtimeEffectiveMs(answerLike[i - 1]);
        if (gap > maxGap) maxGap = gap;
      }
      longestPauseMmSs = formatDurationMmSs(maxGap);
    }

    return {
      sectionId,
      sectionName: String(section?.name ?? ""),
      restarts: countRestartedRealtimeForSection(realtime, sectionId),
      startIso,
      endIso,
      durationMmSs,
      initialPauseMmSs,
      longestPauseMmSs,
    };
  });
}
